import type { FulfillmentConfiguration } from '@ofmos/shared/fulfillment-configuration';
import type { PopUpOption } from '../../../ui';
import { FULFILLMENT } from './configuration';

/**
 * THE OWNER MENU'S OPTIONS (brief FE-FOUNDATION section 10; mockups dashboard-v2-side.html:
 * 124-135, onboarding-v2.html:128-139, clients-v2.html:143-154). Pure.
 *
 * "All clients", "My clients" only when the signed-in person has a team member, a separator,
 * then one entry per owner with the number of rows that option shows on the calling page
 * ("Artur · 41"), the owner with the most clients first, then alphabetically by first name. A
 * page may append its own options after them (Onboarding's "Unassigned", open question
 * Q-ON-1). The button reads "All" for All clients.
 *
 * NO PERSON IS NAMED HERE (coordinator, 4 Oct: the CRM is a platform and OFMOS is its
 * configuration). The order is the data's own, so the coach with the most clients leads on
 * any installation, which on OFMOS's live data is who the mockups put first.
 *
 * REMEMBERED PER BROWSER under the existing key `fulfillment.owner`, shared by the three pages;
 * a stored owner that no longer exists falls back to All (`dashboard/model.ts:103-105`).
 */
export const ALL_OWNERS = 'all';
export const MY_CLIENTS = 'mine';
export const OWNER_STORAGE_KEY = 'fulfillment.owner';

/** One row of the calling page, as far as the owner menu cares. */
export interface OwnedRow {
  readonly ownerId: string | null;
  /** The owner's full name or first name; the menu shows the first name. */
  readonly ownerName: string | null;
}

/** "Artur Kowalski" is "Artur" wherever the pages name a person. */
export function ownerFirstName(name: string | null | undefined): string | null {
  const first = name?.trim().split(/\s+/)[0];
  return first === undefined || first === '' ? null : first;
}

/** Alphabetical by first name: the tie-break after the number of clients. */
export function compareOwnerNames(left: string, right: string): number {
  return left.localeCompare(right);
}

export function ownerOptions(
  rows: readonly OwnedRow[],
  myTeamMemberId: string | null,
  extra: readonly PopUpOption[] = [],
): readonly PopUpOption[] {
  const owners = new Map<string, { label: string; count: number }>();
  for (const row of rows) {
    if (row.ownerId === null) continue;
    const held = owners.get(row.ownerId);
    if (held === undefined) {
      owners.set(row.ownerId, { label: ownerFirstName(row.ownerName) ?? 'Unnamed', count: 1 });
    } else {
      owners.set(row.ownerId, { ...held, count: held.count + 1 });
    }
  }
  const listed = [...owners.entries()]
    .map(([value, entry]) => ({ value, label: entry.label, count: entry.count }))
    .sort((a, b) => b.count - a.count || compareOwnerNames(a.label, b.label));
  const head: PopUpOption[] = [{ value: ALL_OWNERS, label: 'All clients', count: rows.length }];
  if (myTeamMemberId !== null) {
    head.push({
      value: MY_CLIENTS,
      label: 'My clients',
      count: rows.filter((row) => row.ownerId === myTeamMemberId).length,
    });
  }
  return [
    ...head,
    ...listed.map((option, index) => (index === 0 ? { ...option, separatorBefore: true } : option)),
    ...extra,
  ];
}

/**
 * WHERE THE MENU STARTS BEFORE A PERSON HAS CHOSEN (decision page item 13.1 = B: the owner sees
 * everyone, a coach sees their own clients; "Je Rolle eine Einstellung, nicht ein Wert fuer
 * alle"). The configuration names the roles (`ownerMenuStart`); the wider view wins for a person
 * who holds a role of each kind, and a role it does not name starts on All. "My clients" that
 * does not exist for this person (no team member) still reads as All (`resolveOwnerChoice`).
 */
export function ownerStartFor(
  roles: readonly { readonly label: string }[] | undefined,
  config: FulfillmentConfiguration = FULFILLMENT,
): string {
  const starts = (roles ?? []).map((role) => config.ownerMenuStart[role.label]);
  if (starts.includes('all')) return ALL_OWNERS;
  return starts.includes('mine') ? MY_CLIENTS : ALL_OWNERS;
}

/** A stored choice that is no longer an option reads as All. */
export function resolveOwnerChoice(stored: string | null, options: readonly PopUpOption[]): string {
  return stored !== null && options.some((option) => option.value === stored) ? stored : ALL_OWNERS;
}

/** What the button reads: "All" for All clients, the option's own label otherwise. */
export function ownerButtonText(choice: string, options: readonly PopUpOption[]): string {
  if (choice === ALL_OWNERS) return 'All';
  return options.find((option) => option.value === choice)?.label ?? 'All';
}

/**
 * The owner a page filters by: `all`, a team member id, or a page's own value ("Unassigned").
 * "My clients" is the signed-in person's team member.
 */
export function ownerFilterOf(choice: string, myTeamMemberId: string | null): string {
  if (choice === MY_CLIENTS) return myTeamMemberId ?? ALL_OWNERS;
  return choice;
}
